import { type Building, type Cell, type Material, key, MATERIALS } from './city';

/** Original fictional heavy cargo transport. All visible parts are shared,
 * damageable cells, including the four engine pods and their wing pylons. */
export function cargoAirframe(id: number, troopInterior = false): Building {
  const cells = new Map<number, Cell>();
  const olive = 0x778475,
    light = 0x9da99c,
    dark = 0x394644,
    trim = 0xd7d9c0;
  const add = (
    x: number,
    y: number,
    z: number,
    color = olive,
    bounds?: Cell['bounds'],
    material: Material = 'metal',
  ) => {
    const k = key(x, y, z);
    cells.set(k, {
      id: id * 1e6 + k,
      x,
      y,
      z,
      color,
      bounds,
      material,
      role: 'prop',
      hp: MATERIALS[material].strength,
      root: false,
    });
  };
  const box = (x: number, y: number, z: number, w: number, h: number, d: number): Cell['bounds'] => ({
    x,
    y,
    z,
    w,
    h,
    d,
  });
  const inside = (x: number, y: number, z: number) => {
    const taper = z < 5 ? 0.42 + z * 0.116 : z > 24 ? 1 - (z - 24) * 0.09 : 1;
    return ((x + 0.5 - 15.5) / (3.9 * taper)) ** 2 + ((y + 0.5 - 5) / (3.05 * taper)) ** 2 < 1;
  };
  // Hollow rounded shell with a real cargo floor, longerons and repeated ribs.
  for (let z = 1; z < 30; z++)
    for (let x = 11; x <= 19; x++)
      for (let y = 2; y <= 8; y++) {
        if (!inside(x, y, z)) continue;
        if (troopInterior && z >= 26 && x >= 14 && x <= 16 && y >= 4 && y <= 6) continue;
        const shell = [
          [-1, 0],
          [1, 0],
          [0, -1],
          [0, 1],
        ].some(([dx, dy]) => !inside(x + dx, y + dy, z));
        if (!shell && y !== 3 && z !== 1 && z !== 29 && !(z % 5 === 0 && (x === 12 || x === 18))) continue;
        const cockpit = z < 6 && y >= 5 && (x <= 13 || x >= 17 || z === 1),
          bounds = y === 3 && (!shell || troopInterior) ? box(0, 0, 0, 1, 0.16, 1) : undefined;
        add(
          x,
          y,
          z,
          cockpit ? 0x759da3 : y < 4 ? dark : z % 5 === 0 ? light : olive,
          bounds,
          cockpit ? 'glass' : 'metal',
        );
      }
  // Shoulder wings, swept trailing edges and alternating flap panels.
  if (troopInterior) {
    // Two damageable forward flight-control stations. These are occupied by
    // the actual manifest operators, not an extra decorative cockpit crew.
    for (const [x, offset] of [
      [13, 0.14],
      [14, -0.14],
    ]) {
      add(x, 4, 6, dark, box(offset, -0.84, 0.6, 0.4, 0.76, 0.35));
      add(x, 5, 6, 0x759da3, box(offset + 0.035, -1.072, 0.64, 0.33, 0.035, 0.26), 'glass');
    }
  }
  for (let x = 0; x < 31; x++) {
    const offset = Math.abs(x - 15),
      sweep = Math.floor(offset * 0.14),
      chord = offset > 10 ? 3 : 5;
    for (let z = 11 + sweep; z < 11 + sweep + chord; z++) {
      if (x >= 12 && x <= 18) {
        add(x, 6, z, light);
        continue;
      }
      add(x, 6, z, z === 10 + sweep + chord ? dark : light, box(0, 0.08, 0, 1, 0.24, 1));
    }
  }
  // Four distinct turbofan pods. Dark intake rings, empty centre and exhaust.
  for (const cx of [3, 8, 22, 27]) {
    for (let z = 12; z < 17; z++)
      for (let x = cx - 1; x <= cx + 1; x++)
        for (let y = 3; y < 5; y++) {
          if (x === cx && y === 4 && (z === 12 || z === 16)) continue;
          const rim = z === 12 || z === 16;
          add(
            x,
            y,
            z,
            rim ? dark : light,
            box(x < cx ? 0.4 : 0, y === 3 ? 0.25 : 0, 0, x === cx ? 1 : 0.6, y === 4 ? 1 : 0.75, 1),
          );
        }
    add(cx, 5, 14, dark, box(0.32, 0, 0, 0.36, 1, 1));
    add(cx, 6, 14, light, box(0, 0, 0, 1, 0.32, 1));
  }
  // Tall fin and high stabiliser, with fictional cream identification bars.
  for (let y = 7; y < 13; y++)
    for (let z = 25 + Math.floor((y - 7) * 0.45); z < 30; z++)
      add(15, y, z, y === 10 || (y === 11 && z > 27) ? trim : olive, box(0.25, 0, 0, 0.5, 1, 1));
  for (let x = 8; x < 23; x++)
    for (let z = 27; z < 30; z++) if (x !== 15) add(x, 10, z, light, box(0, 0.35, 0, 1, 0.18, 1));
  // Lowered twin-wheel gear, main bogies and a visible rear loading ramp.
  for (const [x, z] of [
    [15, 5],
    [12, 19],
    [18, 19],
    [12, 21],
    [18, 21],
  ]) {
    for (let y = 1; y <= 3; y++) add(x, y, z, light, box(0.38, 0, 0.3, 0.24, 1, 0.35));
    add(x, 0, z, dark, box(0.03, 0.1, 0.1, 0.94, 0.9, 0.8));
  }
  for (let x = 14; x <= 16; x++)
    for (let z = 26; z < 30; z++)
      add(x, 3, z, dark, box(0, troopInterior ? 0 : 0.1 + (z - 26) * 0.14, 0, 1, 0.16, 1));
  // Pallet cargo is destructible interior geometry carried by the same frame.
  for (const z of troopInterior ? [] : [10, 15, 20])
    for (const x of [14, 16]) add(x, 4, z, 0x9a896c, box(0.08, 0, 0.08, 0.84, 0.8, 0.84), 'wood');
  return {
    id,
    name: 'Alder C-38 cargo transport',
    kind: 'aircraft',
    category: 'prop',
    x: 0,
    z: 0,
    w: 31,
    d: 31,
    h: 13,
    color: olive,
    accent: light,
    cells: [...cells.values()],
  };
}
